import {evaluationIsVariant} from '@homeaway/react-experiment';
import {createSelector} from 'reselect';
import {getIsMobile} from './system';
import {getTravelerSegments} from './travelerSegmentFeatures';
import {getMultiUnitsLayoutValues} from './multiUnits';
import {VRBO_WEB_PDP_OPTIMIZE_AMENITIES} from '../abacus/experiments';
import {
    getAbacusTests,
    getTranslations,
    getDynamicLayout as getDynamicLayoutRawData
} from './store';
import getAvailabilityCalendar from './availabilityCalendar';
import {SECTIONS, BLOCKS} from '../constants/layout';
import {generateLayoutOrder, parseMabResponse} from '../utils/layoutHelper';
import {getShowMapSection} from './listingPage';

export const getAmenitiesTestOn = createSelector(
    [getAbacusTests],
    (abacusTests) => {
        return evaluationIsVariant(abacusTests[VRBO_WEB_PDP_OPTIMIZE_AMENITIES], 1);
    }
);

const getIsDynamicLayout = createSelector(
    [getDynamicLayoutRawData],
    (dynamicLayoutRawData) => !!dynamicLayoutRawData
);

/*
 * Returns the config for each section. This will be used by default or dynamic layout
 * Section: {
 *  navLabel: string (Optional) If defined, this is the string that will be displayed in the navigation bar. Omit to hide it from navigation
 *  sectionTitle: string (Optional) If defined, this string will be used to render the headline of the section. Useful if the first block does not include it as part of the content
 *  blocks: array<string> (Required) - Ordered list of blocks that should be rendered in this section
 *  visible: boolean (Optional) - flag to control if the section should be visible (rendered). If not provided, it will default to `true`
 * }
 * */
const getSectionsConfig = createSelector(
    [
        getIsMobile,
        getTranslations,
        getIsDynamicLayout,
        getMultiUnitsLayoutValues,
        getTravelerSegments,
        getAvailabilityCalendar,
        getShowMapSection
    ],
    (
        isMobile,
        translations,
        isDynamicLayout,
        {isHotelType, showMultiUnits},
        {coldStartUser},
        {shouldShowRates},
        showMapSection
    ) => ({
        [SECTIONS.OVERVIEW]: {
            navLabel: translations.navAbout,
            blocks: isMobile
                ? [
                      BLOCKS.INLINE_GLOBAL_MESSAGE,
                      BLOCKS.ABOUT_THE_PROPERTY,
                      BLOCKS.TRIP_DETAILS,
                      BLOCKS.FREE_CANCELLATION,
                      BLOCKS.OWNER_DETAILS_MINI,
                      BLOCKS.BOOKING_ASSISTANCE,
                      BLOCKS.BEAR_STAY_BANNER,
                      BLOCKS.CIVIL_CODE_DISCLAIMER
                  ]
                : [BLOCKS.INLINE_GLOBAL_MESSAGE, BLOCKS.ABOUT_THE_PROPERTY, BLOCKS.TRIP_DETAILS]
        },
        [SECTIONS.INLINE_VIRTUAL_TOUR]: {
            blocks: [BLOCKS.INLINE_VIRTUAL_TOUR]
        },
        [SECTIONS.MIDPAGE_RECO_CAROUSEL]: {
            blocks: [BLOCKS.MIDPAGE_RECO_CAROUSEL],
            visible: coldStartUser && !showMultiUnits
        },
        [SECTIONS.ROOMS_AND_SPACES]: {
            navLabel: translations.navRoomsAndBeds,
            blocks: [BLOCKS.ROOMS_AND_SPACES, isDynamicLayout && BLOCKS.INLINE_VIRTUAL_TOUR]
        },
        [SECTIONS.AMENITIES]: {
            navLabel: translations.navAmenities,
            blocks: [BLOCKS.AMENITIES]
        },
        [SECTIONS.POLICIES]: {
            navLabel: translations.navPolicies,
            sectionTitle: translations.navPolicies,
            blocks: [
                BLOCKS.CANCELLATION_POLICY,
                isMobile && BLOCKS.TRIP_LINK,
                BLOCKS.DAMAGE_POLICY,
                BLOCKS.HOUSE_RULES,
                BLOCKS.CLEANING_PRACTICES
            ]
        },
        [SECTIONS.OTHER_UNITS]: {
            navLabel: isHotelType ? translations.navRooms : translations.navUnits,
            blocks: [BLOCKS.OTHER_UNITS],
            visible: showMultiUnits
        },
        [SECTIONS.REVIEWS]: {
            navLabel: translations.navReviews,
            blocks: [BLOCKS.REVIEWS]
        },
        [SECTIONS.MAP]: {
            navLabel: translations.navMap,
            sectionTitle: translations.navMap,
            blocks: [BLOCKS.MAP],
            visible: showMapSection
        },
        [SECTIONS.CHAT_UI]: {
            blocks: [BLOCKS.CHAT_UI]
        },
        [SECTIONS.HOST]: {
            navLabel: translations['internal-components.navigation-bar.host'],
            blocks: [
                isMobile && !isDynamicLayout && BLOCKS.CHAT_UI,
                BLOCKS.OWNER_DETAILS,
                BLOCKS.AUTH_CARD
            ]
        },
        [SECTIONS.RATES_AVAILABILITY]: {
            navLabel: shouldShowRates
                ? translations.navRatesAvailability
                : translations.navAvailability,
            blocks: [BLOCKS.RATES_AVAILABILITY_CALENDAR]
        }
    })
);

// Returns the default layout sections ordered
const getDefaultLayout = createSelector(
    [getIsMobile, getMultiUnitsLayoutValues, getAmenitiesTestOn],
    (isMobile, {isHotelType}, amenitiesTestOn) => {
        // The conditional rules here apply only control the order.
        // Any flag to control the visibility of the entire section should live within the section config (getSectionsConfig)
        return [
            SECTIONS.OVERVIEW,
            SECTIONS.INLINE_VIRTUAL_TOUR,
            isMobile && SECTIONS.MIDPAGE_RECO_CAROUSEL,
            !isMobile && SECTIONS.MIDPAGE_RECO_CAROUSEL,
            SECTIONS.ROOMS_AND_SPACES,
            isMobile ? amenitiesTestOn && SECTIONS.AMENITIES : SECTIONS.AMENITIES,
            SECTIONS.POLICIES,
            isHotelType && SECTIONS.OTHER_UNITS, // if isHotelType, show the section midPage
            SECTIONS.REVIEWS,
            SECTIONS.MAP,
            !isMobile && SECTIONS.CHAT_UI,
            SECTIONS.HOST,
            SECTIONS.RATES_AVAILABILITY,
            isMobile && !amenitiesTestOn && SECTIONS.AMENITIES,
            !isHotelType && SECTIONS.OTHER_UNITS // If not isHotelType, the section will be rendered at the bottom
        ];
    }
);

export const getDynamicLayout = createSelector(
    [getDynamicLayoutRawData],
    (dynamicLayoutRawData) => {
        // If throttle is OFF (or MAB service failed to return a response) this will be null
        if (!dynamicLayoutRawData) {
            return [];
        }

        // If data available in reducer, throttle is ON and it successfully fetched data from MAB
        // Here happens the parsing logic, append/preppend about the property and other sections that are not part of MAB response
        // Returns array with the sections properly ordered or an empty array if the parsing fails for any reason
        const parsed = parseMabResponse(dynamicLayoutRawData);

        if (parsed.length) {
            return [SECTIONS.OVERVIEW, ...parsed, SECTIONS.OTHER_UNITS];
        }

        return [];
    }
);

const getLayout = createSelector(
    [getDynamicLayout, getDefaultLayout, getSectionsConfig],
    (dynamicLayout, defaultLayout, sectionsConfig) => {
        const isDynamicLayout = !!dynamicLayout.length;
        const layoutOrder = isDynamicLayout ? dynamicLayout : defaultLayout;

        return {
            order: generateLayoutOrder(layoutOrder, sectionsConfig)
        };
    }
);

export default getLayout;
export {getLayout, SECTIONS, BLOCKS};
